<template>
  <div class="fill-width">
    <v-form
      ref="AdmissionMapFilterForm">
      <v-expansion-panels
        class="admission-map-filter"
        v-model="admissionFilterExpanded">
        <v-expansion-panel>
          <v-container
            fluid
            class="mx-0"
            style="padding: 18px 18px 0 !important;">
            <div class="d-flex flex-row flex-wrap">
              <v-col
                cols="12"
                xl="10"
                lg="9"
                md="7"
                class="pa-0"
                :order="$vuetify.breakpoint.smAndDown ? '2' : '1'">
                <v-container class="pa-0 mx-0">
                  <v-row
                    no-gutters
                    class="row-default">
                    <v-col
                      cols="12"
                      xl="3"
                      lg="3"
                      md="6">
                      <PaginatedAutocomplete
                        :label="`${$t('admissionMap.fields.carrier')} *`"
                        :fetch="fetchCarriers"
                        chips
                        small-chips
                        @change="setCarrier"
                        :value="filter.carrierId"
                        :rules="[rule.required]"
                        :loading="loadingCarriers"
                        item-text="name"
                        item-value="id"
                        v-bind="autocompleteAttrs"
                      />
                    </v-col>

                    <v-col
                      cols="12"
                      xl="3"
                      lg="3"
                      md="6">
                      <PaginatedAutocomplete
                        :label="`${$t('admissionMap.fields.contract')} *`"
                        :fetch="fetchContracts"
                        chips
                        small-chips
                        @change="setContract"
                        :refreshListItems="refreshContracts"
                        @listUpdated="refreshContracts = false"
                        :value="filter.contractId"
                        :disabled="!filter.carrierId"
                        :loading="loadingContracts"
                        :rules="[rule.required]"
                        item-text="name"
                        item-value="id"
                        v-bind="autocompleteAttrs"
                      />
                    </v-col>

                    <v-col
                      cols="12"
                      xl="3"
                      lg="3"
                      md="6">
                      <v-menu
                        ref="startDateMenu"
                        v-model="startDateMenu"
                        offset-y
                        max-width="280px"
                        min-width="auto"
                        :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-on="on"
                            v-bind="attrs"
                            class="fill-width">
                            <Textfield
                              :rules="[rule.required]"
                              :persistentPlaceholder="false"
                              v-model="filter.startDate"
                              :label="`${$t('admissionMap.fields.startPeriod')} *`"
                              :placeholder="$t('admissionMap.filter.placeholders.startDate')"
                              readonly
                              regular
                              clearable
                              class="fill-width">
                              <template slot="append">
                                <v-img
                                  src="icons/input-calendar.svg"
                                  style="max-height: 22px;"
                                  contain
                                />
                              </template>
                            </Textfield>
                          </div>
                        </template>

                        <v-date-picker
                          v-model="startDate"
                          :locale="$i18n.locale"
                          color="primary"
                          @change="setStartDate"
                        />
                      </v-menu>
                    </v-col>

                    <v-col
                      cols="12"
                      xl="3"
                      lg="3"
                      md="6">
                      <v-menu
                        ref="endDateMenu"
                        v-model="endDateMenu"
                        offset-y
                        max-width="280px"
                        min-width="auto"
                        :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-on="on"
                            v-bind="attrs"
                            class="fill-width">
                            <Textfield
                              :rules="[rule.required]"
                              :persistentPlaceholder="false"
                              v-model="filter.endDate"
                              :label="`${$t('admissionMap.fields.endPeriod')} *`"
                              :placeholder="$t('admissionMap.filter.placeholders.endDate')"
                              readonly
                              regular
                              clearable
                              class="fill-width">
                              <template slot="append">
                                <v-img
                                  src="icons/input-calendar.svg"
                                  style="max-height: 22px;"
                                  contain
                                />
                              </template>
                            </Textfield>
                          </div>
                        </template>

                        <v-date-picker
                          v-model="endDate"
                          :locale="$i18n.locale"
                          color="primary"
                          @change="setEndDate"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>

              <v-col
                cols="12"
                xl="2"
                lg="3"
                md="5"
                class="fill-height px-0"
                :order="$vuetify.breakpoint.smAndDown ? '1' : '2'">
                <div class="d-flex flex-row align-center justify-end fill-all gap-h-15">
                  <v-badge
                    :content="filterCounter"
                    color="error"
                    overlap
                    right="20"
                    class="badgeCounter"
                    v-if="filterCounter > 0">
                    <v-icon
                      small
                      color="base-color">
                      fas fa-filter
                    </v-icon>
                  </v-badge>
                  <v-icon
                    small
                    color="base-color"
                    v-else>
                    fas fa-filter
                  </v-icon>

                  <span
                    class="base-color">
                    {{ $t('admissionMap.filter.expandSearch') }}
                  </span>

                  <v-icon
                    class="expansionIconClass"
                    :style="expansionIconTransition"
                    @click="expand"
                    color="base-color"
                    size="30">
                    mdi-chevron-up
                  </v-icon>
                </div>
              </v-col>
            </div>
          </v-container>

          <v-expansion-panel-content
            class="admission-map-expansion-content">
            <v-container fluid fill-height style="padding: 0 18px 18px !important;">
              <v-row no-gutters>
                <v-col
                  cols="12"
                  xl="10"
                  lg="9"
                  md="7"
                  class="pa-0">
                  <v-container class="pa-0 mx-0">
                    <v-row
                      no-gutters
                      class="row-default">
                      <v-col
                        cols="12"
                        xl="3"
                        lg="3"
                        md="6">
                        <PaginatedAutocomplete
                          :label="$t('admissionMap.fields.financialGroup')"
                          :fetch="fetchGroups"
                          chips
                          small-chips
                          @change="setFinancialGroup"
                          :refreshListItems="refreshFinancialGroups"
                          @listUpdated="refreshFinancialGroups = false"
                          :value="filter.financialGroupId"
                          :loading="loadingGroups"
                          item-text="name"
                          item-value="id"
                          v-bind="autocompleteAttrs"
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        xl="3"
                        lg="3"
                        md="6">
                        <Textfield
                          :label="$t('admissionMap.fields.patient')"
                          @change="setPatient"
                          :value="filter.patientName"
                          v-bind="textfieldAttrs"
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        xl="3"
                        lg="3"
                        md="6">
                        <PaginatedAutocompleteMulti
                          :label="$t('admissionMap.fields.passwordStatus')"
                          :fetch="fetchPasswords"
                          :value="filter.passwordStatuses"
                          :loading="loadingPasswordStatuses"
                          :refreshListItems="refreshPasswordStatuses"
                          @listUpdated="refreshPasswordStatuses = false"
                          @input="setPasswordStatuses"
                          item-text="name"
                          item-value="name"
                          :groupByHeader="true"
                          v-bind="autocompleteAttrs"
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        xl="3"
                        lg="3"
                        md="6"
                      />
                    </v-row>
                  </v-container>
                </v-col>

                <v-col
                  cols="12"
                  :class="{
                    'd-flex align-end': true,
                  }">
                  <v-row>
                    <v-col
                      v-bind="colActionAttrs"
                      :class="{
                        'd-flex flex-row gap-h-15 justify-end admission-map-action-col': true,
                      }">
                      <v-btn
                        class="admission-map-text-base btn-component primary--text"
                        color="background"
                        @click="resetFilters">
                        {{ $t('admissionMap.filter.clear') }}
                      </v-btn>

                      <v-btn
                        class="admission-map-search-filter-btn btn-component"
                        color="primary"
                        @click="searchRecords"
                        :disabled="!hasFiltersSelected">
                        <div class="mr-2" style="max-width: 18px;">
                          <v-icon
                            size="18"
                            color="base-color">
                            fas fa-search
                          </v-icon>
                        </div>
                        {{ $t('common.search') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-form>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import AdmissionMapService from '@/services-http/servicos/AdmissionMapService';

import PaginatedAutocompleteMulti from '../Default/PaginatedAutocompleteMulti.vue';
import PaginatedAutocomplete from '../Default/PaginatedAutocomplete.vue';
import Autocomplete from '@/components/Default/Autocomplete.vue';
import Textfield from '@/components/Default/Textfield.vue';
import { AUTOCOMPLETE_ATTRS } from '@/shared/constants/admissionMap/DefaultConstants';
import {
  FILTER_ACTION_COL,
  FILTER_COL,
} from '@/shared/constants/admissionMap/FilterConstants';
import { TEXTFIELD_ATTRS } from '@/shared/constants/invoiceParameter/DefaultConstants';
import Rules from '@/shared/validators/formRules';

export default {
  name: 'AdmissionMapFilter',
  components: {
    PaginatedAutocompleteMulti,
    PaginatedAutocomplete,
    Autocomplete,
    Textfield,
  },
  data() {
    return {
      startDateMenu: false,
      startDate: null,

      endDateMenu: false,
      endDate: null,

      refreshFinancialGroups: false,
      refreshContracts: false,
      refreshPasswordStatuses: false,

      admissionFilterExpanded: false,
      rule: null,

      admissionMapService: null,
    };
  },
  computed: {
    ...mapGetters({
      filter: 'admission-map/filter',
      groups: 'admission-map/groups',
      carriers: 'admission-map/carriers',
      contracts: 'admission-map/contracts',
      passwordStatusOptions: 'admission-map/passwordStatusOptions',
      hasFiltersSelected: 'admission-map/hasFiltersSelected',
      loadingPasswordStatuses: 'admission-map/loadingPasswordStatuses',
      loadingGroups: 'admission-map/loadingGroups',
      loadingCarriers: 'admission-map/loadingCarriers',
      loadingContracts: 'admission-map/loadingContracts',
    }),
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: false,
        placeholder: this.$t('admissionMap.filter.placeholders.select'),
      };
    },
    colAttrs() {
      return FILTER_COL;
    },
    colActionAttrs() {
      return FILTER_ACTION_COL;
    },
    textfieldAttrs() {
      return {
        ...TEXTFIELD_ATTRS,
        borderSelected: false,
        placeholder: this.$t('admissionMap.filter.placeholders.select'),
      };
    },
    filterCounter() {
      const counter = Object.values(this.filter).filter(value =>
        value !== null &&
        value !== undefined &&
        !(typeof value === 'string' && value.trim() === '') &&
        !(Array.isArray(value) && value.length === 0)
      ).length;

      return counter;
    },
    expansionIconTransition() {
			return {
        transform: this.admissionFilterExpanded !== 0 ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.25s',
      };
		},
  },
  beforeDestroy() {
    if (this.patientSearchTimeout) {
      clearTimeout(this.patientSearchTimeout);
    }
  },
  methods: {
    ...mapMutations({
      setGroupsLoading: 'admission-map/SET_GROUPS_LOADING',
      setCarriersLoading: 'admission-map/SET_CARRIERS_LOADING',
      setContractsLoading: 'admission-map/SET_CONTRACTS_LOADING',
    }),
    ...mapActions({
      clearTableData: 'admission-map/clearTableData',
      fetchPasswordStatuses: 'admission-map/fetchPasswordStatuses',
      resetStoreFilter: 'admission-map/resetFilter',
      applyFilter: 'admission-map/applyFilter',
      searchByFilter: 'admission-map/searchByFilter',
    }),
    async fetchCarriers(page, size, inputValue) {
      try {
        this.setCarriersLoading(true);

        const params = new URLSearchParams();
        params.append('filterName', inputValue);
        params.append('page', page);
        params.append('size', size);

        const data = await this.admissionMapService.fetchAdmissionCarriers(params).then((res) => res.data);

        return data;
      } catch (err) {
      } finally {
        this.setCarriersLoading(false);
      }
    },
    async fetchGroups(page, size, inputValue) {
      try {
        this.setGroupsLoading(true);

        const params = new URLSearchParams();
        params.append('filterName', inputValue);
        params.append('page', page);
        params.append('size', size);

        const data = await this.admissionMapService.fetchAdmissionGroups(params).then((res) => res.data);

        return data;
      } catch (err) {
      } finally {
        this.setGroupsLoading(false);
      }
    },
    async fetchContracts(page, size, inputValue) {
      if (!this.filter.carrierId && !this.filter.financialGroupId) {
        return [];
      }

      try {
        this.setContractsLoading(true);
        const params = new URLSearchParams();
        if (inputValue) {
          params.append('filterName', inputValue);
        }
        if (this.filter.carrierId) {
          params.append('carrierId', this.filter.carrierId);
        }
        if (this.filter.financialGroupId) {
          params.append('financialGroupId', this.filter.financialGroupId);
        }
        params.append('page', page);
        params.append('size', size);

        const data = await this.admissionMapService.fetchAdmissionContracts(params).then((res) => res.data);

        return data;
      } catch (err) {
      } finally {
        this.setContractsLoading(false);
      }
    },
    async fetchPasswords(page, size, inputValue) {
      const normalized = await this.fetchPasswordStatuses();

      const result = {
        content: normalized,
        last: true,
      }

      return result;
    },
    triggerListRefresh(flagName) {
      this[flagName] = false;

      this.$nextTick(() => {
        this[flagName] = true;
      });
    },
    async setFinancialGroup(financialGroupId) {
      this.applyFilter({ financialGroupId: financialGroupId, contractId: null });

      setTimeout(() => {
        this.refreshContracts = true;
      }, 1000);
    },
    async setCarrier(carrierId) {
      await this.clearTableData();
      this.applyFilter({
        carrierId: carrierId,
        financialGroupId: null,
        contractId: null,
        passwordStatuses: [],
      });

      this.triggerListRefresh('refreshFinancialGroups');
      this.triggerListRefresh('refreshContracts');
      this.triggerListRefresh('refreshPasswordStatuses');

      if (carrierId) {
        await this.searchRecords();
      }
    },
    setPasswordStatuses(status) {
      this.applyFilter({ passwordStatuses: status || [] });
    },
    async setContract(contractId) {
      this.applyFilter({ contractId });

      if (contractId) {
        await this.searchRecords();
      }
    },
    setPatient(patientName) {
      this.applyFilter({ patientName });
    },
    async setStartDate(startDate) {
      this.applyFilter({ startDate: moment(startDate, "YYYY-MM-DD").format("DD/MM/YYYY") || null });

      if (startDate) {
        this.startDate = startDate;
        this.startDateMenu = false;

        await this.searchRecords();
      }
    },
    async setEndDate(endDate) {
      this.applyFilter({ endDate: moment(endDate, "YYYY-MM-DD").format("DD/MM/YYYY") || null });
      if (endDate) {
        this.endDate = endDate;
        this.endDateMenu = false;

        await this.searchRecords();
      }
    },
    async searchRecords() {
      this.$refs.AdmissionMapFilterForm.validate();

      const { carrierId, contractId, startDate, endDate } = this.filter;

      const hasRequiredFields =
        !!carrierId &&
        !!contractId &&
        !!startDate &&
        !!endDate;

      if (!hasRequiredFields) {
        return;
      }

      await this.searchByFilter();
    },
    resetFilters() {
      this.resetStoreFilter();
    },
    expand() {
			if (this.admissionFilterExpanded === 0) {
				this.admissionFilterExpanded = undefined;

        return;
			}

      this.admissionFilterExpanded = 0;
		},
  },
  created() {
    this.rule = new Rules();
    this.admissionMapService = new AdmissionMapService();
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/admissionMap/default.scss";
@import "@/shared/styles/admissionMap/filter.scss";
</style>
